/**
 * DriveSync — Persistência do Sumário Comentado no Google Drive.
 *
 * Padrão: backup-merge-write + write queue serializada + retry com backoff.
 * Segue o padrão de projects.ts (withProjectLock).
 *
 * O summaryDocument vive dentro do project.json.
 * Merge opera EXCLUSIVAMENTE no campo summaryDocument — nunca sobrescreve o resto.
 */

import { getTokenOrRefresh } from '../../services/googleAuth';
import { validateBeforeSave } from '../../services/googleDrive/core';
import { driveFetch } from '../../services/googleDrive/driveFetch';
import { logger } from '../../utils/logger';
import { auditLog } from '../../utils/auditLog';
import { stampSchema, migrateIfNeeded } from '../../services/googleDrive/schemaMigration';
import { useSumarioComentadoStore } from '../../stores/sumarioComentadoStore';
import type { SCSummaryDocument } from '../../types/sumarioComentado.types';

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const BACKOFF_DELAYS = [1000, 3000, 8000]; // retry: 1s → 3s → 8s

// ---------- Write Queue (per projectId) ----------
const _writeLocks = new Map<string, Promise<void>>();

function withWriteLock<T>(projectId: string, fn: () => Promise<T>): Promise<T> {
  const prev = _writeLocks.get(projectId) ?? Promise.resolve();
  // Chain: wait for previous, then run fn (whether prev succeeded or failed)
  let resolve!: (value: T) => void;
  let reject!: (error: unknown) => void;
  const result = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
  const next = prev.then(() => fn().then(resolve, reject), () => fn().then(resolve, reject));
  // Suppress unhandled rejection on the queue chain itself
  _writeLocks.set(projectId, next.then(() => {}, () => {}));
  return result;
}

// ---------- Token helper ----------
async function getToken(): Promise<string> {
  return getTokenOrRefresh();
}

export class TokenExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenExpiredError';
  }
}

// ---------- Core API ----------

/**
 * Read project.json and extract summaryDocument.
 * Returns null if field doesn't exist (nullable by design).
 */
export async function readSummaryDocument(projectFolderId: string): Promise<SCSummaryDocument | null> {
  const data = await readProjectJson(projectFolderId);
  const raw = (data.summaryDocument as SCSummaryDocument | undefined) ?? null;
  if (!raw) return null;
  const { data: migrated } = migrateIfNeeded(raw, 'summaryDocument');
  auditLog({ operation: 'sync', target: 'summaryDocument', detail: `Sumário carregado do Drive (projeto ${projectFolderId})`, success: true });
  return migrated as SCSummaryDocument;
}

/**
 * Full READ-MERGE-WRITE cycle. This is the only way to save.
 * Always enqueued via write lock — never two concurrent writes per project.
 */
export async function saveSummaryDocument(
  projectFolderId: string,
  summaryDocument: SCSummaryDocument
): Promise<void> {
  return withWriteLock(projectFolderId, async () => {
    await saveSummaryDocumentInternal(projectFolderId, summaryDocument, 0);
  });
}

// ---------- Internal implementation ----------

async function saveSummaryDocumentInternal(
  projectFolderId: string,
  summaryDocument: SCSummaryDocument,
  attempt: number
): Promise<void> {
  // Loop-based retry instead of recursion to avoid unhandled rejections
  let currentAttempt = attempt;
  while (true) {
    try {
      // 1. READ project.json completo
      const snapshot = await readProjectJson(projectFolderId);

      // 2. MERGE: substituir APENAS summaryDocument (stamp schemaVersion)
      const merged = {
        ...snapshot,
        summaryDocument: stampSchema(summaryDocument),
        updatedAt: new Date().toISOString(),
      };

      // 3. VALIDATE: todos campos de primeiro nível preservados
      validateMerge(snapshot, merged);

      // 4. VALIDATE serialization (Padrão 1 do CLAUDE.md)
      validateBeforeSave(merged, 'DriveSync.saveSummaryDocument');

      // 5. WRITE
      await writeProjectJson(projectFolderId, merged);

      // Success
      useSumarioComentadoStore.getState().setSyncStatus('saved');
      auditLog({ operation: 'save', target: 'summaryDocument', detail: `Sumário salvo no Drive (projeto ${projectFolderId})`, success: true });
      return;
    } catch (error) {
      if (error instanceof TokenExpiredError) {
        useSumarioComentadoStore.getState().setSyncStatus('error');
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Sessão expirada. Faça login novamente para salvar.');
        }).catch(() => {});
        throw error;
      }

      if (isRetryableError(error) && currentAttempt < BACKOFF_DELAYS.length) {
        await _delayFn(BACKOFF_DELAYS[currentAttempt]);
        currentAttempt++;
        // Continue loop — full READ-MERGE-WRITE, never resend stale payload
        continue;
      }

      // Exhausted retries or non-retryable
      useSumarioComentadoStore.getState().setSyncStatus('error');
      logger.error('[DriveSync]', 'Save failed after retries:', error);
      // Notify researcher — sync failure must never be silent (Regra 9)
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Erro ao salvar o Sumário. Verifique sua conexão.');
      }).catch(() => {});
      throw error;
    }
  }
}

/**
 * Validate that merge preserved all original first-level fields.
 * If any field from snapshot is missing in merged → abort.
 */
export function validateMerge(
  snapshot: Record<string, unknown>,
  merged: Record<string, unknown>
): void {
  const snapshotKeys = Object.keys(snapshot);
  const missingKeys = snapshotKeys.filter((key) => !(key in merged));
  if (missingKeys.length > 0) {
    throw new Error(
      `[DriveSync] Merge integrity check failed: missing keys [${missingKeys.join(', ')}]`
    );
  }
}

// ---------- Low-level Drive operations ----------

async function readProjectJson(projectFolderId: string): Promise<Record<string, unknown>> {
  const token = await getToken();

  // Find project.json
  const query = `name='project.json' and '${projectFolderId}' in parents and trashed=false`;
  const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

  const searchResponse = await driveFetch(searchUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  handleHttpError(searchResponse);
  const searchData = await searchResponse.json();

  if (!searchData.files || searchData.files.length === 0) {
    throw new Error('project.json não encontrado');
  }

  const fileId = searchData.files[0].id;
  const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
  const downloadResponse = await driveFetch(downloadUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  handleHttpError(downloadResponse);
  const text = await downloadResponse.text();

  try {
    return JSON.parse(text);
  } catch {
    throw new Error('project.json contém JSON inválido');
  }
}

async function writeProjectJson(
  projectFolderId: string,
  data: Record<string, unknown>
): Promise<void> {
  const token = await getToken();

  // Find project.json file ID
  const query = `name='project.json' and '${projectFolderId}' in parents and trashed=false`;
  const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

  const searchResponse = await driveFetch(searchUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  handleHttpError(searchResponse);
  const searchData = await searchResponse.json();
  const fileId = searchData.files?.[0]?.id;

  if (!fileId) {
    throw new Error('project.json não encontrado para escrita');
  }

  // PATCH content
  const patchUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
  const patchResponse = await driveFetch(patchUrl, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data, null, 2),
  });

  handleHttpError(patchResponse);
}

// ---------- Helpers ----------

function handleHttpError(response: Response): void {
  if (response.ok) return;
  if (response.status === 401 || response.status === 403) {
    throw new TokenExpiredError('Sessão expirada. Faça login novamente.');
  }
  if (response.status === 429 || response.status === 503) {
    const err = new Error(`HTTP ${response.status}: ${response.statusText}`);
    (err as RetryableHttpError).retryable = true;
    throw err;
  }
  throw new Error(`HTTP ${response.status}: ${response.statusText}`);
}

interface RetryableHttpError extends Error {
  retryable: boolean;
}

function isRetryableError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'retryable' in error) {
    return (error as RetryableHttpError).retryable === true;
  }
  return false;
}

/** Delay function — overridable via _testing for unit tests */
let _delayFn = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// ---------- Debounce timers ----------
const _debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Debounced save for text edits (5s) or discrete actions (1s).
 * Each call resets the timer. Fires saveSummaryDocument when timer expires.
 */
export function debouncedSave(
  projectFolderId: string,
  type: 'text' | 'discrete'
): void {
  const key = `save-${projectFolderId}`;
  const existing = _debounceTimers.get(key);
  if (existing) clearTimeout(existing);

  const delayMs = type === 'text' ? 5000 : 1000;

  useSumarioComentadoStore.getState().setSyncStatus('saving');

  const timer = setTimeout(() => {
    _debounceTimers.delete(key);
    const doc = useSumarioComentadoStore.getState().getDocument();
    saveSummaryDocument(projectFolderId, doc).catch((error) => {
      if (!(error instanceof TokenExpiredError)) {
        logger.error('[DriveSync]', 'Debounced save failed:', error);
      }
    });
  }, delayMs);

  _debounceTimers.set(key, timer);
}

/**
 * Immediate save — used on project switch or window close.
 * No debounce, but still queued.
 */
export function immediateSave(projectFolderId: string): Promise<void> {
  const key = `save-${projectFolderId}`;
  const existing = _debounceTimers.get(key);
  if (existing) {
    clearTimeout(existing);
    _debounceTimers.delete(key);
  }

  const doc = useSumarioComentadoStore.getState().getDocument();

  // Safety: never overwrite Drive with completely empty document
  // This guards against saving after loadFromDrive(null) or store reset
  if (doc.sections.length === 0 && doc.inbox.length === 0) {
    logger.warn('[DriveSync]', 'immediateSave skipped — document is empty, refusing to overwrite Drive');
    return Promise.resolve();
  }

  useSumarioComentadoStore.getState().setSyncStatus('saving');
  return saveSummaryDocument(projectFolderId, doc);
}

/**
 * Cancel all pending debounce timers (for cleanup on unmount).
 */
export function cancelPendingSaves(): void {
  for (const timer of _debounceTimers.values()) {
    clearTimeout(timer);
  }
  _debounceTimers.clear();
}

/**
 * Flush pending saves: cancel debounce timers and immediately persist.
 * Use on navigation (not tab close) to ensure data is saved, not discarded — A8.
 */
export function flushPendingSaves(projectFolderId: string): Promise<void> {
  cancelPendingSaves();
  return immediateSave(projectFolderId);
}

/**
 * Flush ALL pending sumário saves across all projects.
 * Used in beforeunload — iterates _debounceTimers to find pending project IDs.
 */
export function flushAllPendingSumarioSaves(): void {
  const pendingProjectIds: string[] = [];
  for (const key of _debounceTimers.keys()) {
    // Keys are "save-{projectFolderId}"
    const projectId = key.replace('save-', '');
    if (projectId) pendingProjectIds.push(projectId);
  }
  cancelPendingSaves();
  for (const projectId of pendingProjectIds) {
    // Fire-and-forget — beforeunload cannot await
    immediateSave(projectId).catch((err) => {
      logger.error('[DriveSync]', `flushAll failed for project ${projectId}: ${err}`);
    });
  }
}

// Export internals for testing
export const _testing = {
  withWriteLock,
  readProjectJson,
  writeProjectJson,
  validateMerge,
  BACKOFF_DELAYS,
  getToken,
  saveSummaryDocumentInternal,
  /** Override delay function for tests (set to instant to avoid fake timer issues) */
  setDelayFn: (fn: (ms: number) => Promise<void>) => { _delayFn = fn; },
  resetDelayFn: () => { _delayFn = (ms) => new Promise((r) => setTimeout(r, ms)); },
};
